import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrTrainingCourse } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export function TrainingCourseSelect({
  label = 'Course',
  value = '',
  onChange,
  placeholder,
  className,
  required,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const [courses, setCourses] = useState<HrTrainingCourse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listTrainingCourses()
      .then(setCourses)
      .catch((err) => showApiError(err, t('errors.failedToLoadCourses')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      courses.map((course) => ({
        value: String(course.id),
        label: course.category ? `${course.title} (${course.category})` : course.title,
      })),
    [courses],
  );

  return (
    <SearchableSelect
      className={className}
      label={label}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectCourse}
      loading={loading}
      searchPlaceholder={selectLabels.searchCourses}
      emptyMessage={selectLabels.noCourses}
    />
  );
}
